import { useCallback, useRef, useState } from "react";

/**
 * Centralized guard for modals/drawers with unsaved user input.
 *
 * Usage:
 *   const guard = useUnsavedChangesGuard(isDirty, () => actuallyClose());
 *   <Dialog open={open} onOpenChange={guard.guardOpenChange(setOpen)}>
 *     <DialogContent dirty={isDirty} onRequestClose={guard.requestClose}>
 *       ...
 *       <Button onClick={guard.requestClose}>Cancel</Button>
 *     </DialogContent>
 *   </Dialog>
 *   {guard.confirmNode}
 *
 * - Outside-click and Escape are blocked by the primitive when `dirty` is true.
 * - X / Cancel / programmatic close all funnel through `requestClose`, which
 *   prompts the user before discarding.
 * - When the user confirms "Discard Changes", `onDiscard` runs (close + clear draft).
 */
export function useUnsavedChangesGuard(
  dirty: boolean,
  onDiscard: () => void,
) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const requestClose = useCallback(() => {
    if (dirtyRef.current) {
      setConfirmOpen(true);
    } else {
      onDiscard();
    }
  }, [onDiscard]);

  const guardOpenChange = useCallback(
    (setOpen: (v: boolean) => void) => (next: boolean) => {
      if (next) {
        setOpen(true);
        return;
      }
      if (dirtyRef.current) {
        setConfirmOpen(true);
      } else {
        setOpen(false);
      }
    },
    [],
  );

  const confirmDiscard = useCallback(() => {
    setConfirmOpen(false);
    onDiscard();
  }, [onDiscard]);

  const keepEditing = useCallback(() => setConfirmOpen(false), []);

  return {
    requestClose,
    guardOpenChange,
    confirmOpen,
    setConfirmOpen,
    confirmDiscard,
    keepEditing,
  };
}
